import { createAsyncThunk } from '@reduxjs/toolkit';
import {HOSTNAME} from "../configs/Domain";
import _ from "lodash";
import axios from "axios";


// Define the async thunk
export const fetchFilteredTickers = createAsyncThunk(
  'tickers/fetchFiltered',
  async (mongoQuery, { rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/tickers/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(mongoQuery),
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const fetchTickerData = createAsyncThunk(
  'tickers/fetchTickerData',
  async ({ symbols, fields, collection }, { getState }) => {
    const state = getState().tickers;
    if (!fields.includes('symbol')) {
      fields.unshift('symbol');
    }

    let symbolsToFetch = symbols.filter(symbol => {
      const existingTicker = state.tickers[symbol];
      return !existingTicker || fields.some(field => !_.has(existingTicker, field));
    });

    if (symbolsToFetch.length === 0) {
      return { fetchedTickers: [], badTickers: [] };
    }

    let params = {
      symbols: symbolsToFetch.join(','),
      fields: fields.join(',')
    }
    if (collection) {
      params.collection = collection;
    }

    const response = await axios.get(`${HOSTNAME}/tickers/find`, { params: params });
    const fetchedSymbols = response.data.map(ticker => ticker.symbol);
    const badTickers = symbolsToFetch.filter(symbol => !fetchedSymbols.includes(symbol));

    return { fetchedTickers: response.data, badTickers };
  }
);


export const fetchQuotes = createAsyncThunk(
  'tickers/fetchQuotes',
  async (symbols, { getState, rejectWithValue }) => {
    const state = getState().tickers;

    // Filter out symbols that already have quotes
    const symbolsToFetch = symbols.filter(symbol => !state.quotes[symbol]);

    // If all symbols are already loaded, skip the API call
    if (symbolsToFetch.length === 0) return { fetchedQuotes: {}, badTickers: [] };

    try {
      const params = { symbols: symbolsToFetch.join(',') };
      const response = await axios.get(`${HOSTNAME}/tickers/quotes`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch quotes');
    }
  }
);






export const fetchETFs = createAsyncThunk('etfs/fetchETFs', async (symbols, { getState }) => {
  const queryString = symbols.join(',');
  const response = await fetch(`${HOSTNAME}/tickers/find?symbols=${queryString}&fields=symbol,name,holdings,tags,metrics,specs,stats.summary.ytd,stats.benchmarks.spy.corr_1_yr,logo`);
  if (!response.ok) throw new Error('Failed to fetch ETF data');
  return response.json();
});


export const fetchXrayData = createAsyncThunk(
  'tickers/fetchXrayData',
  async (symbols, { rejectWithValue }) => {
      const queryStringFromObject = (obj)=>{
          return Object.keys(obj).map(key => `${key}=${obj[key]}`).join('&');
      }
    try {
      let finalWeights = {};

      if (!Array.isArray(symbols) && typeof symbols === 'object') {
        // Assuming you have a helper to convert object to query string
            finalWeights = symbols;
      } else if (Array.isArray(symbols)) {
        let equalWeight = 1 / symbols.length;
        symbols.forEach(x=>{
            finalWeights[x] = equalWeight;
        });
      } else {
        throw new Error('Invalid input for symbols');
      }
      const queryString = queryStringFromObject(finalWeights);

      const response = await axios.get(`${HOSTNAME}/tickers/xray?${queryString}`);
      if (!response.data) throw new Error('No data received from server');

      const data = response.data;
      // Assuming 'parents' is a valid key that needs to be modified
      if (data.parents) {
        for (let k in data.parents) {
          if (finalWeights.hasOwnProperty(k)) {
            data.parents[k].wgt = finalWeights[k];
          }
        }
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);



export const fetchUpcomingEvents = createAsyncThunk('etfs/fetchUpcomingEvents', async (tickers, { getState }) => {
    const { user } = getState();
    if (user.upcomingEvents){
        return user.upcomingEvents;
    }
    const queryString = `symbols=${tickers.join(',')}`;

    const response = await fetch(`${HOSTNAME}/tickers/calendar?${queryString}`);
    if (!response.ok) throw new Error('Failed to fetch ETF data');
    const data = await response.json();
    return data;
});

export const fetchTickerNews = createAsyncThunk(
  'news/fetchTickerNews',
  async (symbols, { getState, rejectWithValue }) => {
    const { news } = getState(); // Access the news slice state
    // const unfetchedSymbols = symbols.filter(symbol => !news.fetchedSymbols.includes(symbol));
    const tickerPageIndex = news.tickerPageIndex;

    // If all symbols have been fetched already, no need to make a request
    // if (unfetchedSymbols.length === 0) {
    //   return []; // Return an empty array since there's nothing new to fetch
    // }

    try {
      const response = await fetch(`${HOSTNAME}/tickers/news?symbols=${symbols.join(',')}&page=${tickerPageIndex}`);
      if (!response.ok) throw new Error('Network response was not ok');

      const data = await response.json();
      return { symbols: symbols, newsData: data }; // Include both symbols and news data in the response
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const listHedgeFunds = createAsyncThunk(
  'tickers/listHedgeFunds',
  async (_, { getState, rejectWithValue }) => {
    const state = getState().tickers;

    // Avoid re-fetching if the data already exists
    if (state.hedgeFunds.length > 0) {
      return state.hedgeFunds; // Return existing data to prevent double-fetching
    }

    try {
      const response = await axios.get(`${HOSTNAME}/tickers/list/hedge-funds`);
      if (!response.data) {
        throw new Error('Failed to fetch hedge funds data');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error fetching hedge funds');
    }
  }
);

export const fetchHedgeFund = createAsyncThunk(
  'tickers/fetchHedgeFund',
  async (hedgeFundName, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(`${HOSTNAME}/tickers/hedge-funds/${hedgeFundName}`);
      if (!response.data) {
        throw new Error('Failed to fetch hedge fund data');
      }
      return { name: hedgeFundName, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || `Error fetching hedge fund ${hedgeFundName}`);
    }
  }
);


export const fetchStockFinancials = createAsyncThunk(
  'tickers/fetchStockFinancials',
  async ({ symbol, startDate, endDate }, { getState, rejectWithValue }) => {
    try {
      const state = getState().tickers;
      const existingData = state.tickers[symbol]?.financials;

      // Check if we already have the data
      if (existingData?.length > 0) {
        // If no date range specified or we have data in range, return early
        if (!startDate && !endDate) {
          return { financials: [], symbol };
        }

        const hasDateRange = existingData.some(entry => {
          const entryDate = entry.timestamp.split('T')[0];
          if (startDate && entryDate < startDate) return false;
          if (endDate && entryDate > endDate) return false;
          return true;
        });

        if (hasDateRange) {
          return { financials: [], symbol };
        }
      }

      // Build query string
      let queryParams = `symbol=${symbol}`;
      if (startDate) queryParams += `&start_date=${startDate}`;
      if (endDate) queryParams += `&end_date=${endDate}`;

      const response = await axios.get(`${HOSTNAME}/timeseries/financials?${queryParams}`);

      // API returns array directly, so we'll pass it through
      return {
        financials: response.data,
        symbol
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch financials');
    }
  }
);

